<template>
  <div>
    <el-row :gutter="15">
      <el-col :md="6">
        <el-card shadow="never">
          <div slot="header">
            <span style="font-size: 14px;">Filters</span>
          </div>
          <campaign-filters
            :filters.sync="filters"
            @reset="resetFilters"
            @validated="applyFilters"
          />
        </el-card>
      </el-col>
      <el-col :md="18">
        <el-card
          shadow="never"
          class="mb-3"
        >
          <el-tabs v-model="activeName">
            <el-tab-pane
              label="Profiles"
              name="profiles"
            >
              <span slot="label">
                <el-badge
                  :value="profiles.total || 0"
                >
                  Profiles
                </el-badge>
              </span>
              <campaign-profiles
                v-loading="isLoadingProfiles"
                :profiles="profiles"
              />
              <br>
              <div style="text-align: center;">
                <el-pagination
                  :current-page.sync="profiles.page"
                  :page-size.sync="profiles.limit"
                  layout="total, sizes, prev, pager, next, jumper"
                  :total="profiles.total"
                  @size-change="reloadProfiles"
                  @current-change="reloadProfiles"
                />
              </div>
            </el-tab-pane>
            <el-tab-pane
              label="Billing Contacts"
              name="billings"
            >
              <span slot="label">
                <el-badge
                  :value="billingContacts.total || 0"
                >
                  Billing Contacts
                </el-badge>
              </span>
              <campaign-billings
                v-loading="isLoadingBillingContacts"
                :billing-contacts="billingContacts"
              />
              <br>
              <div style="text-align: center;">
                <el-pagination
                  :current-page.sync="billingContacts.page"
                  :page-size.sync="billingContacts.limit"
                  layout="total, sizes, prev, pager, next, jumper"
                  :total="billingContacts.total"
                  @size-change="reloadBillings"
                  @current-change="reloadBillings"
                />
              </div>
            </el-tab-pane>
            <el-tab-pane
              label="Additional Emails"
              name="additionals"
            >
              <span slot="label">
                <el-badge
                  :value="additionals.length"
                >
                  Additional Emails
                </el-badge>
              </span>
              <campaign-additionals :additionals.sync="additionals" />
            </el-tab-pane>
          </el-tabs>
        </el-card>
        <el-card
          v-loading="isLoading"
          shadow="never"
        >
          <div slot="header">
            <span style="font-size: 14px;">Email Form</span>
          </div>
          <el-form
            size="small"
          >
            <el-form-item
              label="Email Template"
              required
            >
              <el-select
                v-model="template"
                placeholder="Select a Email Template"
                value-key="id"
                :loading="isLoadingTemplates"
              >
                <el-option
                  v-for="t in templates"
                  :key="t.id"
                  :label="t.email_header_text"
                  :value="t"
                />
              </el-select>
            </el-form-item>
          </el-form>
          <template v-if="template">
            <campaign-form
              ref="campaignForm"
              :form="campaignForm"
            />
            <el-row>
              <el-col style="text-align: right;">
                <el-button
                  size="small"
                  @click.native="resetCampaignForm"
                >
                  Reset
                </el-button>
                <el-button
                  type="primary"
                  size="small"
                  @click.native="send"
                >
                  Send
                </el-button>
              </el-col>
            </el-row>
          </template>
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import CampaignFilters from '@/components/Application/CustomerService/Campaigns/CampaignFilters'
import CampaignProfiles from '@/components/Application/CustomerService/Campaigns/CampaignProfiles'
import CampaignBillings from '@/components/Application/CustomerService/Campaigns/CampaignBillings'
import CampaignAdditionals from '@/components/Application/CustomerService/Campaigns/CampaignAdditionals'
import CampaignForm from '@/components/Application/CustomerService/Campaigns/CampaignForm'
export default {
  components: {
    CampaignFilters,
    CampaignProfiles,
    CampaignBillings,
    CampaignAdditionals,
    CampaignForm
  },
  data () {
    return {
      title: 'Campaigns',
      isLoading: false,
      filters: { sendTo: [] },
      activeName: 'profiles',
      profiles: [],
      isLoadingProfiles: false,
      billingContacts: [],
      isLoadingBillingContacts: false,
      additionals: [],
      template: null,
      templates: [],
      isLoadingTemplates: false,
      campaignForm: {},
      isAllowSend: true,
      breadcrumb: {
        name: 'Campaigns',
        route: { name: 'customerservice-campaigns' }
      }
    }
  },
  created () {
    this.loadTemplates()
  },
  methods: {
    async loadTemplates () {
      try {
        this.isLoadingTemplates = true
        const types = await this.$sdk.notificationtemplatetype.list()
        const campaignType = types.find(type => type.description === 'Campaign')
        this.templates = await this.$sdk.notificationtemplate.list(builder => {
          builder.filters = { type: campaignType.id }
        })
      } catch {
        this.$message.error('An error occurred while fetching email templates')
      } finally {
        this.isLoadingTemplates = false
      }
    },
    applyFilters () {
      this.profiles = []
      this.billingContacts = []
      if (this.filters.sendTo.find(s => s === 'Users')) {
        this.getProfiles()
      }
      if (this.filters.sendTo.find(s => s === 'Billing Contacts')) {
        this.getBillingContacts()
      }
    },
    getProfiles () {
      this.isLoadingProfiles = true
      this.$sdk.profile.list(builder => {
        builder.filters = {
          account: this.filters.accounts.map(account => account.id).join(','),
          carrier: this.filters.carriers.map(carrier => carrier.id).join(',')
        }
        builder.limit = 10
      }).then(res => {
        this.profiles = res
      })
        .finally(() => { this.isLoadingProfiles = false })
    },
    getBillingContacts () {
      this.isLoadingBillingContacts = true
      this.$sdk.accountcontact.list(builder => {
        builder.filters = {
          account: this.filters.accounts.map(account => account.id).join(','),
          carrier: this.filters.carriers.map(carrier => carrier.id).join(','),
          include_billing: 1
        }
        builder.limit = 10
      }).then(res => {
        this.billingContacts = res
      })
        .finally(() => { this.isLoadingBillingContacts = false })
    },
    async reloadProfiles () {
      try {
        this.isLoadingProfiles = true
        await this.profiles.reload()
      } catch {
        this.$message.error('An error occurred while loading the profiles')
      } finally {
        this.isLoadingProfiles = false
      }
    },
    async reloadBillings () {
      try {
        this.isLoadingBillingContacts = true
        await this.billingContacts.reload()
      } catch {
        this.$message.error('An error occurred while loading the billing contacts')
      } finally {
        this.isLoadingBillingContacts = false
      }
    },
    send () {
      if (this.$_.isEmpty(this.filters.sendTo)) {
        this.$message.error('[Send To] is required in the filter')
        return
      }
      this.$refs.campaignForm.validate().then(validated => {
        if (validated) {
          this.isLoading = true
          const campaign = this.getCampaignObject()
          campaign.create()
            .then(() => { this.$message.success('Emails have been successfully sent') })
            .finally(() => { this.isLoading = false })
        }
      })
    },
    getCampaignObject () {
      const campaign = new this.$sdk.notificationcampaign()
      campaign.template = this.template.id
      campaign.accounts = this.filters.accounts.map(account => account.id)
      campaign.carriers = this.filters.carriers.map(carrier => carrier.id)
      campaign.emails = this.additionals
      campaign.billing = !!this.filters.sendTo.find(s => s === 'Billing Contacts')
      campaign.users = !!this.filters.sendTo.find(s => s === 'Users')
      campaign.reply_to = this.campaignForm.reply_to ? this.campaignForm.reply_to.trim() : ''
      campaign.sender_name = this.campaignForm.sender_name ? this.campaignForm.sender_name.trim() : ''
      campaign.subject = this.campaignForm.subject ? this.campaignForm.subject.trim() : ''
      campaign.greeting = this.campaignForm.greeting ? this.campaignForm.greeting.trim() : ''
      campaign.message = this.campaignForm.message ? this.campaignForm.message.trim() : ''
      return campaign
    },
    resetCampaignForm () {
      Object.keys(this.campaignForm).map(key => {
        this.campaignForm[key] = ''
      })
    },
    resetFilters () {
      this.$set(this.filters, 'accounts', [])
      this.$set(this.filters, 'carriers', [])
      this.$set(this.filters, 'sendTo', [])
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep .el-tabs__nav-scroll {
  padding-top: 10px;
}
</style>
